/**
 * Content Sanitization Utilities
 * 
 * Ensures consistent layout and prevents typography issues
 * in article content before rendering.
 */

/**
 * Sanitize article content for layout safety
 * 
 * - Converts non-breaking spaces to regular spaces for proper wrapping
 * - Removes problematic inline styles
 * - Strips manual line breaks inside paragraphs
 * - Normalizes whitespace
 * - Ensures clean semantic HTML
 * 
 * @param {string} content - Raw HTML content
 * @returns {string} - Sanitized HTML content
 */
export function sanitizeArticleContent(content) {
  if (!content || typeof content !== 'string') {
    return content;
  }

  let sanitized = content;

  // 0. Convert non-breaking spaces to regular spaces for proper word wrapping
  // This is critical - Quill editor often inserts &nbsp; which prevents natural line breaks
  sanitized = sanitized.replace(/&nbsp;/gi, ' ');
  sanitized = sanitized.replace(/\u00A0/g, ' '); // Unicode non-breaking space
  
  // 1. Remove problematic inline width styles
  sanitized = sanitized.replace(/style="[^"]*width:\s*\d+[^"]*"/gi, '');
  
  // 2. Remove inline font-size that could break hierarchy
  sanitized = sanitized.replace(/style="[^"]*font-size:\s*[^"]*"/gi, '');
  
  // 3. Remove <br> tags inside paragraphs (keep them between block elements)
  sanitized = sanitized.replace(/<p>([^<]*)<br\s*\/?>\s*([^<]*)<\/p>/gi, '<p>$1 $2</p>');
  
  // 4. Clean up multiple consecutive <br> tags
  sanitized = sanitized.replace(/(<br\s*\/?>\s*){2,}/gi, '<br>');
  
  // 5. Remove empty paragraphs
  sanitized = sanitized.replace(/<p>\s*<\/p>/gi, '');
  sanitized = sanitized.replace(/<p><br\s*\/?><\/p>/gi, '');
  
  // 6. Normalize multiple spaces (but preserve single spaces)
  sanitized = sanitized.replace(/  +/g, ' ');
  
  // 7. Clean up whitespace around block elements
  sanitized = sanitized.replace(/\s*(<\/?(p|h[1-6]|blockquote|ul|ol|li|div|hr)>)\s*/gi, '$1');
  
  return sanitized.trim();
}

/**
 * Process content with citation markers
 * Updates citation anchor IDs for navigation
 * 
 * @param {string} content - HTML content with citation markers
 * @param {number} citationCount - Total number of citations
 * @returns {string} - Processed content with proper anchors
 */
export function processContentWithCitations(content, citationCount) {
  if (!content) return content;
  
  let processedContent = content;
  
  // Pattern for citations with data-citation attribute
  const citationRegex = /<sup><a[^>]*class="citation-marker"[^>]*data-citation="(\d+)"[^>]*>\[(\d+)\]<\/a><\/sup>/gi;
  
  processedContent = processedContent.replace(citationRegex, (match, dataCitation) => {
    const citationNum = parseInt(dataCitation, 10);
    if (citationNum > 0 && citationNum <= citationCount) {
      return `<sup><a href="#ref-${citationNum}" class="citation-marker" data-citation="${citationNum}" id="cite-${citationNum}">[${citationNum}]</a></sup>`;
    }
    return match;
  });
  
  // Handle simpler patterns: <a href="#ref-X">[X]</a>
  const simpleRefRegex = /<a\s+href="#ref-(\d+)"[^>]*>\[(\d+)\]<\/a>/gi;
  processedContent = processedContent.replace(simpleRefRegex, (match, refNum) => {
    const citationNum = parseInt(refNum, 10);
    if (citationNum > 0 && citationNum <= citationCount) {
      return `<sup><a href="#ref-${citationNum}" class="citation-marker" id="cite-${citationNum}">[${citationNum}]</a></sup>`;
    }
    return match;
  });
  
  return processedContent;
}

/**
 * Prepare article content for display
 * Combines sanitization and citation processing
 * 
 * @param {string} content - Raw article content
 * @param {Array} citations - Array of citation strings
 * @returns {string} - Fully processed content ready for rendering
 */
export function prepareArticleContent(content, citations = []) {
  if (!content) return '';
  
  // Step 1: Sanitize for layout safety
  let prepared = sanitizeArticleContent(content);
  
  // Step 2: Process citation markers
  prepared = processContentWithCitations(prepared, citations.length);
  
  return prepared;
}

export default {
  sanitizeArticleContent,
  processContentWithCitations,
  prepareArticleContent
};
